import { useQuery } from '@tanstack/react-query'
import { Modal, Table, Input } from 'antd'
import moment from 'moment'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useDebounce } from '@hooks/useDebounce'
import { fetchOutgoingPayments } from '@utils/fetchData'
import { numberWithSpacesIntl } from '@utils/numberWithSpaces'

const VendorPaymentsModal = ({ visible, onClose, cardCode, cardName, initialDateFrom, initialDateTo }) => {
	const { t } = useTranslation()
	const [dateFrom, setDateFrom] = useState(initialDateFrom || '')
	const [dateTo, setDateTo] = useState(initialDateTo || '')
	const [docNum, setDocNum] = useState('')

	const debouncedDocNum = useDebounce(docNum, 500)

	const [pageSize] = useState(10)
	const [page, setPage] = useState(0)

	const { data = [], isLoading } = useQuery({
		queryKey: [
			'outgoing-payments',
			page,
			cardCode,
			debouncedDocNum,
			dateFrom,
			dateTo,
		],
		queryFn: fetchOutgoingPayments,
		enabled: visible && !!cardCode,
		keepPreviousData: true,
		refetchOnWindowFocus: false,
	})

	const columns = [
		{
			title: t('docNum'),
			dataIndex: 'docNum',
			key: 'docNum',
		},
		{
			title: t('docDate'),
			dataIndex: 'docDate',
			key: 'docDate',
			render: (val) => (val ? moment(val).format('DD.MM.YYYY') : '-'),
		},
		{
			title: t('cashAcctName'),
			dataIndex: 'cashAcctName',
			key: 'cashAcctName',
		},
		{
			title: t('cardName'),
			dataIndex: 'cardName',
			key: 'cardName',
		},
		{
			title: t('cashSum'),
			dataIndex: 'cashSum',
			key: 'cashSum',
			render: (val) => numberWithSpacesIntl(val || 0),
		},
		{
			title: t('currency'),
			dataIndex: 'docCurr',
			key: 'docCurr',
		},
		{
			title: t('dds'),
			dataIndex: 'u_DdsArticle',
			key: 'u_DdsArticle',
		},
		{
			title: t('comments'),
			dataIndex: 'comments',
			key: 'comments',
		},
	]

	return (
		<Modal
			title={`${t('outgoingPayment')} - ${cardName}`}
			open={visible}
			onCancel={onClose}
			footer={null}
			width={1200}
		>
			<div className="grid grid-cols-2 lg:grid-cols-4 items-end gap-4 mb-6">
				<div className="flex flex-col gap-1">
					<p className="font-nunito">{t('startDate')}</p>
					<Input
						type="date"
						value={dateFrom}
						onChange={(e) => {
							setDateFrom(e.target.value)
							setPage(0)
						}}
						allowClear
					/>
				</div>
				<div className="flex flex-col gap-1">
					<p className="font-nunito">{t('endDate')}</p>
					<Input
						type="date"
						value={dateTo}
						onChange={(e) => {
							setDateTo(e.target.value)
							setPage(0)
						}}
						allowClear
					/>
				</div>
				<div className="flex flex-col gap-1">
					<p className="font-nunito">{t('docNum')}</p>
					<Input
						value={docNum}
						onChange={(e) => {
							setDocNum(e.target.value)
							setPage(0)
						}}
						allowClear
						placeholder={t('docNum')}
					/>
				</div>
			</div>
			<div className="flex justify-end items-center mb-4">
				<div className="mt-2 flex sm:mr-10">
					<button
						onClick={() => setPage((prev) => Math.max(0, prev - 1))}
						disabled={page === 0}
						className="h-[30px] w-[110px] bg-[#0A4D68] text-white disabled:bg-gray-200 disabled:text-gray-400 "
					>
						{t('previous')}
					</button>
					<div className="bg-[#099204]  w-[20px] h-[30px] flex items-center  justify-center">
						<span className="text-center text-white">{page + 1}</span>
					</div>
					<button
						onClick={() => setPage((prev) => prev + 1)}
						disabled={data?.length < pageSize}
						className="h-[30px] w-[110px] bg-[#0A4D68] text-white disabled:bg-gray-200 disabled:text-gray-400 "
					>
						{t('next')}
					</button>
				</div>
			</div>
			<Table
				dataSource={data}
				columns={columns}
				loading={isLoading}
				pagination={false}
				rowKey={(record, index) => record.docEntry || index}
				scroll={{ x: 'max-content' }}
			/>
		</Modal>
	)
}

export default VendorPaymentsModal
